import React from 'react';
import {Box, Grid, Paper} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {AlertDialog, LoadingPage} from 'components';
import {H6} from 'components/TypographyShortcuts';
import useWindowSize from 'hooks/useWindowSize';
import InfoModal from 'components/widgets/tooltips/InfoModal';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        border: `1px solid ${theme.palette.gray.light}`,
    },
    container: {
        height: '100%',
        padding: '1em',
    },
    noticeMessage: {
        color: theme.palette.secondary.main,
        fontWeight: 'bold',
    },
    titleContainer: {},
    highlight: {
        border: `1px solid ${theme.palette.secondary.main}`,
    },
}));

const DefaultNoDataComponent = ({children}) => {
    const classes = useStyles();
    return <H6 className={classes.noticeMessage}>{children}</H6>;
};

const CardContent = ({
    hasData = true,
    isLoading = false,
    error = null,
    noDataMessage = 'No data for this yet',
    NoDataComponent = DefaultNoDataComponent,
    children,
}) => {
    if (isLoading == null || isLoading) {
        return <LoadingPage />;
    } else if (error) {
        return (
            <>
                <NoDataComponent>
                    Data could not be loaded at this time.
                </NoDataComponent>
                {process.env.NODE_ENV === 'development' ? (
                    <AlertDialog message={error} title='Problem!' />
                ) : (
                    window.dataLayer.push({
                        event: 'data_load_error',
                        data: {
                            url: window.location.href,
                            endpoint: error?.endpoint,
                            status: error?.status,
                        },
                    })
                )}
            </>
        );
    } else if (hasData) {
        return children || null;
    } else {
        return <NoDataComponent>{noDataMessage}</NoDataComponent>;
    }
};

const WidgetCard = ({
    title,
    alignTitle = 'center',
    spacing = 1,
    allowOverflow = false,
    children,
    hasData = true,
    isLoading = false,
    error = null,
    clearCard = false,
    noDataMessage,
    NoDataComponent,
    limitHeight = false,
    infoModalContent,
    xs = true,
    sm,
    md,
    lg,
    xl,
    style,
    className = '',
    highlight,
    ...gridProps
}) => {
    const classes = useStyles();
    const Canvas = clearCard ? Box : Paper;
    const {width} = useWindowSize();

    if (hasData) {
        return (
            <Grid item {...{xs, sm, md, lg, xl}}>
                <Canvas
                    className={`${clearCard ? null : classes.root} ${className} ${
                        highlight ? classes.highlight : ''
                    }`}
                    square={clearCard ? null : true}
                    style={{
                        overflow: allowOverflow ? 'auto hidden' : 'hidden',
                        height: limitHeight
                            ? width >= 1280
                                ? '550px'
                                : null
                            : null,
                        ...style,
                    }}
                    {...gridProps}
                >
                    <Box className={classes.container}>
                        <Grid container spacing={spacing}>
                            {title && (
                                <Grid container className={classes.titleContainer}>
                                    <Grid
                                        item
                                        style={{
                                            display: 'flex',
                                            justifyContent: alignTitle,
                                        }}
                                        xs={12}
                                    >
                                        <H6>{title}</H6>
                                        <InfoModal content={infoModalContent} />
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <CardContent
                                    hasData={hasData}
                                    isLoading={isLoading}
                                    error={error}
                                    noDataMessage={noDataMessage}
                                    NoDataComponent={NoDataComponent}
                                >
                                    {children}
                                </CardContent>
                            </Grid>
                        </Grid>
                    </Box>
                </Canvas>
            </Grid>
        );
    } else {
        return null;
    }
};

export default WidgetCard;
export {CardContent};
